import { useState } from "react";
import { Flower2, Crosshair, Droplets, Hand, Palette, Leaf, HeartPulse, Wind, Check, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal, SectionHeading } from "./Reveal";
import { THERAPIES, waLink } from "@/lib/site";

const ICONS = {
  "yoga-therapy": Flower2,
  acupuncture: Crosshair,
  "cupping-hijama": Droplets,
  sujok: Hand,
  "colour-therapy": Palette,
  "ayurveda-naturopathy": Leaf,
  "cancer-rehab-yoga": HeartPulse,
  "pranayama-mudra-meditation": Wind,
};

export const Therapies = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="therapies" data-testid="therapies-section" className="relative bg-[#F8FFF8] py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHeading
          testId="therapies-heading"
          overline="Integrative Therapies Offered"
          title="Eight disciplines, one intention — your wellbeing"
          description="Every therapy at NAVYOGA is practised by Dr. Rajni Ahuja herself, blending classical training with a gentle, personal approach. Tap any therapy to learn more."
        />

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {THERAPIES.map((therapy, i) => {
            const Icon = ICONS[therapy.id];
            return (
              <Reveal key={therapy.id} delay={0.06 * (i % 4)}>
                <button
                  data-testid={`therapy-card-${therapy.id}`}
                  onClick={() => setActive(therapy)}
                  className="group flex h-full w-full flex-col rounded-2xl border border-emerald-900/10 bg-white p-7 text-left transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-[#2E8B57]/40 hover:shadow-[0_24px_50px_-20px_rgba(46,139,87,0.35)]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#2E8B57]/10 text-[#2E8B57] transition-colors duration-300 group-hover:bg-[#2E8B57] group-hover:text-white">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-heading mt-5 text-xl font-semibold text-slate-900">{therapy.name}</h3>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-[#1E3A8A]/70">{therapy.tagline}</p>
                  <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-600">{therapy.description}</p>
                  <span className="mt-auto pt-5 text-xs font-bold uppercase tracking-[0.2em] text-[#2E8B57] opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    Learn more →
                  </span>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent data-testid="therapy-detail-modal" className="max-w-lg rounded-3xl border-emerald-900/10 bg-[#F8FFF8] p-8">
          {active && (
            <>
              <DialogHeader>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#2E8B57] text-white">
                  {(() => {
                    const Icon = ICONS[active.id];
                    return <Icon className="h-6 w-6" />;
                  })()}
                </div>
                <DialogTitle className="font-heading mt-4 text-2xl font-semibold text-slate-900">{active.name}</DialogTitle>
                <DialogDescription className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
                  {active.description}
                </DialogDescription>
              </DialogHeader>
              <ul className="mt-5 space-y-2.5">
                {active.benefits.map((b) => (
                  <li key={b} className="flex items-center gap-3 text-sm text-slate-700">
                    <Check className="h-4 w-4 shrink-0 text-[#2E8B57]" />
                    {b}
                  </li>
                ))}
              </ul>
              <a
                data-testid="therapy-modal-whatsapp-cta"
                href={waLink(`Namaste! I would like to book a session for ${active.name} at NAVYOGA.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 flex items-center justify-center gap-2 rounded-full bg-[#2E8B57] px-6 py-3.5 text-sm font-semibold text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#267a4b]"
              >
                <MessageCircle className="h-4 w-4" />
                Book on WhatsApp
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
